import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Inject,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { SHORT_BRAND_NAME } from '@rd/core/tokens/rd-lib.token';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { WestpacNavLink, WestpacNavLinkGroup } from '../../types';

@Component({
  selector: 'rd-westpac-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WestpacHeaderComponent {
  @HostBinding('class.rd-westpac-header') componentClass = true;

  navLinkGroups: WestpacNavLinkGroup[] = [
    {
      name: 'wl.westpac.header.discover_exclusive_benefits',
      links: [
        {
          name: 'wl.westpac.header.special_offers',
          url: '/offers',
        },
      ],
    },
    {
      name: 'wl.westpac.header.buy_in_app_with_discount',
      links: [
        {
          name: 'wl.westpac.header.hotels',
          url: this.urlUtilsService.travelEdgeUrl().toString(),
        },
        {
          name: 'wl.westpac.header.sidemenu.gift_cards',
          url: '/gift_card',
        },
      ],
    },
    {
      name: 'wl.westpac.header.my_offers_account',
      links: [
        {
          name: 'wl.westpac.header.manage_my_hotel_bookings',
          url: this.urlUtilsService
            .travelEdgeUrl('/account/booking')
            .toString(),
        },
        {
          name: 'wl.westpac.header.sidemenu.manage_gift_cards',
          url: '/gift_card/manage',
        },
      ],
    },
  ];

  // BehaviorSubject to know that the user changed the nav group
  changedNavGroup$: BehaviorSubject<any> = new BehaviorSubject(null);

  // We change the selected nav group depending on if the user clicked the nav link group or if the page redirects.
  // Typically, the page redirect case will only trigger if the user accesses /gift_card via URL (as the user usually
  // will click/tab over the nav link group to move around the app).
  selectedNavLinkGroup$: Observable<WestpacNavLinkGroup> = combineLatest([
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      // https://kaligo.atlassian.net/browse/BUG-484
      // Timing issue where the page has already rendered before this selectedNavLinkGroup has changed already
      // We have to set a default URL so this observable starts with something.
      startWith({ url: this.router.url }),
    ),
    this.changedNavGroup$,
  ]).pipe(
    map(([routerEvent, changedNavGroup]) => {
      if (changedNavGroup) {
        return changedNavGroup;
      }

      // @ts-ignore TS2339
      const { url } = routerEvent;

      if (url.startsWith('/gift_card/manage')) {
        return this.navLinkGroups[2];
      } else if (url.startsWith('/gift_card')) {
        return this.navLinkGroups[1];
      }

      return this.navLinkGroups[0];
    }),
  );

  vm$ = combineLatest([this.selectedNavLinkGroup$]).pipe(
    map(([selectedNavLinkGroup]) => {
      return {
        selectedNavLinkGroup,
      };
    }),
  );

  constructor(
    @Inject(SHORT_BRAND_NAME) private shortBrandName: string,
    protected router: Router,
    protected urlUtilsService: UrlUtilsService,
  ) {}

  logoImageUrl(): string {
    return this.urlUtilsService.imageUrl('/images/rd/header-logo.png');
  }

  getBrandLogoName(): string {
    return `${this.shortBrandName.toLowerCase()}-logo`;
  }

  // View helper methods

  isActiveNavLinkGroup(
    navLink: WestpacNavLinkGroup,
    selected: WestpacNavLinkGroup,
  ): boolean {
    if (selected) {
      return selected.name === navLink.name;
    }
    return false;
  }

  isActiveNavLink(navLink: WestpacNavLink): boolean {
    return this.router.url.startsWith(navLink.url);
  }

  isExternalNavLink(navLink: WestpacNavLink): boolean {
    return (
      navLink.url.toString().startsWith('http://') ||
      navLink.url.toString().startsWith('https://')
    );
  }

  setSelectedNavLinkGroup(navLinkGroup: WestpacNavLinkGroup): void {
    this.changedNavGroup$.next(navLinkGroup);
  }
}
